<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';
import NewHook from '../NewHook.vue';

const SECRET_FIELD_PATTERN = /token|password|secret/i;

const PROVIDERS = [
  {
    id: 'tablefever',
    i18nKey: 'TABLEFEVER',
    icon: 'i-lucide-utensils',
    logo: 'tablefever.png',
  },
  {
    id: 'sevenrooms',
    i18nKey: 'SEVENROOMS',
    icon: 'i-lucide-calendar-check',
    logo: 'sevenrooms.png',
  },
];

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const uiFlags = computed(() => getters['integrations/getUIFlags']?.value || {});
const getIntegration = getters['integrations/getIntegration'];

const isLoading = computed(() => uiFlags.value.isFetching);
const showProviderMenu = ref(false);
const showAddHookModal = ref(false);
const showDetailsModal = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedProviderId = ref(null);
const selectedProvider = ref(null);
const selectedHook = ref(null);
const selectedHookDetails = ref([]);

const isSecretField = name => SECRET_FIELD_PATTERN.test(name || '');

const buildDetails = (integration, hook) => {
  const formSchema = integration.settings_form_schema || [];
  const labelByName = Object.fromEntries(
    formSchema.map(field => [field.name, field.label])
  );
  const visibleProperties = integration.visible_properties || [];
  const secretProperties = formSchema
    .map(field => field.name)
    .filter(name => isSecretField(name) && !visibleProperties.includes(name));
  const propertyNames = [...visibleProperties, ...secretProperties];

  return propertyNames.map(property => ({
    label: labelByName[property] || property,
    value: hook?.settings?.[property] || '',
    masked: isSecretField(property),
  }));
};

const hookDisplayName = hook =>
  hook?.settings?.name ||
  hook?.settings?.venue_id ||
  hook?.settings?.resto_hash_id ||
  `#${hook?.id}`;

const providers = computed(() =>
  PROVIDERS.map(provider => {
    const integration = getIntegration.value(provider.id) || {};
    const hooks = integration.hooks || [];

    return {
      ...provider,
      name: t(
        `INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDERS.${provider.i18nKey}.NAME`
      ),
      description: t(
        `INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDERS.${provider.i18nKey}.DESCRIPTION`
      ),
      enabled: hooks.length > 0,
      hooks,
      integration,
    };
  })
);

const copyDetailValue = async value => {
  await copyTextToClipboard(value);
  useAlert(t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.COPIED'));
};

onMounted(() => {
  store.dispatch('integrations/get');
});

const toggleProviderMenu = () => {
  showProviderMenu.value = !showProviderMenu.value;
};

const closeProviderMenu = () => {
  showProviderMenu.value = false;
};

const openConnectModal = providerId => {
  selectedProviderId.value = providerId;
  showProviderMenu.value = false;
  showAddHookModal.value = true;
};

const hideConnectModal = () => {
  showAddHookModal.value = false;
  selectedProviderId.value = null;
};

const openDetailsModal = (provider, hook) => {
  selectedProvider.value = provider;
  selectedHook.value = hook;
  selectedHookDetails.value = buildDetails(provider.integration, hook);
  showDetailsModal.value = true;
};

const hideDetailsModal = () => {
  showDetailsModal.value = false;
  selectedProvider.value = null;
  selectedHook.value = null;
  selectedHookDetails.value = [];
};

const openDisconnectPopup = hook => {
  selectedHook.value = hook;
  showDeleteConfirmationPopup.value = true;
};

const closeDisconnectPopup = () => {
  showDeleteConfirmationPopup.value = false;
  selectedHook.value = null;
};

const confirmDisconnect = async () => {
  if (!selectedHook.value) return;

  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: selectedHook.value.id,
      appId: selectedHook.value.app_id,
    });
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
    closeDisconnectPopup();
    hideDetailsModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.HEADER')"
    :button-label="$t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.ADD_BUTTON')"
    @click="toggleProviderMenu"
    @close="closeProviderMenu"
  >
    <template #action>
      <div
        v-if="showProviderMenu"
        class="absolute top-full ltr:right-0 rtl:left-0 mt-2 z-20 min-w-[16rem] rounded-xl border border-n-weak bg-n-solid-1 p-2 shadow-lg"
      >
        <button
          v-for="provider in providers"
          :key="provider.id"
          type="button"
          class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-n-alpha-2"
          @click="openConnectModal(provider.id)"
        >
          <img
            :src="`/dashboard/images/integrations/${provider.logo}`"
            :alt="provider.name"
            class="h-8 w-8 rounded-md border border-n-weak object-contain"
          />
          <span class="text-sm font-medium text-n-slate-12">
            {{ provider.name }}
          </span>
        </button>
      </div>
    </template>

    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>
    <template v-else>
      <p class="mb-6 text-n-slate-11">
        {{ $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PAGE_DESCRIPTION') }}
      </p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div
          v-for="provider in providers"
          :key="provider.id"
          class="flex flex-col rounded-md bg-n-alpha-3 p-6 shadow outline outline-1 outline-n-container"
        >
          <div class="mb-4 flex items-start justify-between">
            <div class="flex h-12 w-12">
              <img
                :src="`/dashboard/images/integrations/${provider.logo}`"
                class="max-w-full rounded-md border border-n-weak bg-n-alpha-3 object-contain shadow-sm dark:bg-n-alpha-2"
                :alt="provider.name"
              />
            </div>
            <span
              class="flex h-5 w-5 items-center justify-center rounded-full p-0.5 text-white"
              :class="provider.enabled ? 'bg-n-teal-9' : 'bg-n-slate-8'"
            >
              <i class="i-ph-check-bold text-sm" />
            </span>
          </div>
          <div class="mb-2 flex items-center justify-between gap-3">
            <span class="text-base font-semibold text-n-slate-12">
              {{ provider.name }}
            </span>
          </div>
          <p class="mb-4 text-sm text-n-slate-11">
            {{ provider.description }}
          </p>

          <div
            v-if="provider.hooks.length"
            class="mt-auto flex flex-col gap-2"
          >
            <div
              v-for="hook in provider.hooks"
              :key="hook.id"
              class="flex items-center justify-between gap-2 rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
            >
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-n-slate-12">
                  {{ hookDisplayName(hook) }}
                </p>
                <p
                  v-if="hook.settings?.slug"
                  class="truncate text-xs text-n-slate-10"
                >
                  {{ hook.settings.slug }}
                </p>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <Button
                  slate
                  faded
                  size="sm"
                  :label="
                    $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.VIEW_DETAILS')
                  "
                  @click="openDetailsModal(provider, hook)"
                />
                <Button
                  ruby
                  faded
                  size="sm"
                  :label="$t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT')"
                  @click="openDisconnectPopup(hook)"
                />
              </div>
            </div>
          </div>
          <p v-else class="mt-auto text-sm text-n-slate-10">
            {{ $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDER_EMPTY') }}
          </p>
        </div>
      </div>
    </template>

    <woot-modal v-model:show="showAddHookModal" :on-close="hideConnectModal">
      <NewHook
        v-if="selectedProviderId"
        :integration-id="selectedProviderId"
        @close="hideConnectModal"
      />
    </woot-modal>

    <woot-modal v-model:show="showDetailsModal" :on-close="hideDetailsModal">
      <div v-if="selectedProvider && selectedHook" class="flex h-auto flex-col">
        <woot-modal-header
          :header-title="hookDisplayName(selectedHook)"
          :header-content="
            $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.DETAILS_DESCRIPTION')
          "
        />
        <div class="px-8 pb-8">
          <dl class="flex flex-col gap-4">
            <div
              v-for="detail in selectedHookDetails"
              :key="detail.label"
              class="flex flex-col gap-1"
            >
              <dt
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ detail.label }}
              </dt>
              <dd v-if="detail.masked">
                <AccessToken
                  :value="detail.value || '--'"
                  :show-reset-button="false"
                  :copy-label="
                    $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.COPY')
                  "
                  @on-copy="copyDetailValue"
                />
              </dd>
              <dd v-else class="break-all text-sm text-n-slate-12">
                {{ detail.value || '--' }}
              </dd>
            </div>
          </dl>
          <div class="mt-6 flex justify-end">
            <Button
              slate
              faded
              :label="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
              @click="hideDetailsModal"
            />
          </div>
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDisconnectPopup"
      :on-confirm="confirmDisconnect"
      :title="$t('INTEGRATION_APPS.DELETE.TITLE.ACCOUNT')"
      :message="$t('INTEGRATION_APPS.DELETE.MESSAGE.ACCOUNT')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.ACCOUNT')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </SettingsLayout>
</template>
